import { useLocation } from "@tanstack/react-router";

import type { BlogLocale } from "@/features/blog/locales";
import { isAlternateLocale } from "@/features/blog/locales";

/**
 * The site's shared words (nav, footer, the email + Join field, the trust
 * line and the closing Join band) in every landing locale, so a translated
 * page isn't wrapped in English. French uses tu, Spanish tú, like the platform
 * pages; French puts a no-break space before : ; ? !
 */
export type SiteCopy = {
  features: string;
  blog: string;
  docs: string;
  signIn: string;
  join: string;
  platforms: string;
  agents: string;
  useCases: string;
  resources: string;
  company: string;
  affiliates: string;
  terms: string;
  privacy: string;
  acceptableUse: string;
  tagline: string;
  searchProfiles: string;
  sendMessages: string;
  analyzePosts: string;
  marketingAgents: string;
  multiAccount: string;
  competitorAnalysis: string;
  placeholder: string;
  trustStrong: string;
  trustRest: string;
  joinTitle: string;
  joinSub: string;
  emailLabel: string;
  /** The award pill above the hero headline. */
  award: string;
  /** Label before the agent logos under the hero. */
  worksFrom: string;
};

const EN: SiteCopy = {
  features: "Features",
  blog: "Blog",
  docs: "Docs",
  signIn: "Sign in",
  join: "Join",
  platforms: "Platforms",
  agents: "Agents",
  useCases: "Use cases",
  resources: "Resources",
  company: "Company",
  affiliates: "Affiliates",
  terms: "Terms",
  privacy: "Privacy",
  acceptableUse: "Acceptable use",
  tagline: "The agentic browser for social media.",
  searchProfiles: "Search profiles",
  sendMessages: "Send messages",
  analyzePosts: "Analyze posts & comments",
  marketingAgents: "AI agents for marketing",
  multiAccount: "Multi-account",
  competitorAnalysis: "Competitor analysis",
  placeholder: "you@email.com",
  trustStrong: "Undetectable",
  trustRest: ", designed to keep your accounts safe and secure.",
  joinTitle: "Give your agent a browser.",
  joinSub:
    "Join and your agent works LinkedIn, Sales Navigator, X and Reddit for you, in the background.",
  emailLabel: "Email address",
  award: "AI Agent tool of 2026",
  worksFrom: "Works from",
};

const SITE_COPY: Record<BlogLocale, SiteCopy> = {
  en: EN,
  fr: {
    features: "Fonctionnalités",
    blog: "Blog",
    docs: "Docs",
    signIn: "Connexion",
    join: "Rejoindre",
    platforms: "Plateformes",
    agents: "Agents",
    useCases: "Cas d’usage",
    resources: "Ressources",
    company: "Entreprise",
    affiliates: "Affiliation",
    terms: "Conditions",
    privacy: "Confidentialité",
    acceptableUse: "Usage acceptable",
    tagline: "Le navigateur agentique pour les réseaux sociaux.",
    searchProfiles: "Rechercher des profils",
    sendMessages: "Envoyer des messages",
    analyzePosts: "Analyser posts et commentaires",
    marketingAgents: "Agents IA pour le marketing",
    multiAccount: "Multi-compte",
    competitorAnalysis: "Veille concurrentielle",
    placeholder: "toi@email.com",
    trustStrong: "Indétectable",
    trustRest: ", conçu pour garder tes comptes sûrs et sécurisés.",
    joinTitle: "Donne un navigateur à ton agent.",
    joinSub:
      "Rejoins Wonda et ton agent gère LinkedIn, Sales Navigator, X et Reddit pour toi, en arrière-plan.",
    emailLabel: "Adresse e-mail",
    award: "Outil d’agent IA de 2026",
    worksFrom: "Fonctionne depuis",
  },
  es: {
    features: "Funciones",
    blog: "Blog",
    docs: "Docs",
    signIn: "Iniciar sesión",
    join: "Unirse",
    platforms: "Plataformas",
    agents: "Agentes",
    useCases: "Casos de uso",
    resources: "Recursos",
    company: "Empresa",
    affiliates: "Afiliados",
    terms: "Términos",
    privacy: "Privacidad",
    acceptableUse: "Uso aceptable",
    tagline: "El navegador agéntico para redes sociales.",
    searchProfiles: "Buscar perfiles",
    sendMessages: "Enviar mensajes",
    analyzePosts: "Analizar posts y comentarios",
    marketingAgents: "Agentes de IA para marketing",
    multiAccount: "Multicuenta",
    competitorAnalysis: "Análisis de competencia",
    placeholder: "tu@email.com",
    trustStrong: "Indetectable",
    trustRest: ", diseñado para mantener tus cuentas seguras y protegidas.",
    joinTitle: "Dale un navegador a tu agente.",
    joinSub:
      "Únete y tu agente trabaja LinkedIn, Sales Navigator, X y Reddit por ti, en segundo plano.",
    emailLabel: "Correo electrónico",
    award: "Herramienta de agente de IA de 2026",
    worksFrom: "Funciona desde",
  },
  de: {
    features: "Funktionen",
    blog: "Blog",
    docs: "Docs",
    signIn: "Anmelden",
    join: "Beitreten",
    platforms: "Plattformen",
    agents: "Agenten",
    useCases: "Anwendungsfälle",
    resources: "Ressourcen",
    company: "Unternehmen",
    affiliates: "Partnerprogramm",
    terms: "AGB",
    privacy: "Datenschutz",
    acceptableUse: "Nutzungsrichtlinie",
    tagline: "Der agentische Browser für Social Media.",
    searchProfiles: "Profile suchen",
    sendMessages: "Nachrichten senden",
    analyzePosts: "Posts und Kommentare analysieren",
    marketingAgents: "KI-Agenten für Marketing",
    multiAccount: "Mehrere Konten",
    competitorAnalysis: "Wettbewerbsanalyse",
    placeholder: "du@email.com",
    trustStrong: "Nicht erkennbar",
    trustRest: ", gebaut, damit deine Konten sicher bleiben.",
    joinTitle: "Gib deinem Agenten einen Browser.",
    joinSub:
      "Tritt bei, und dein Agent erledigt LinkedIn, Sales Navigator, X und Reddit für dich, im Hintergrund.",
    emailLabel: "E-Mail-Adresse",
    award: "KI-Agenten-Tool 2026",
    worksFrom: "Funktioniert mit",
  },
  ro: {
    features: "Funcții",
    blog: "Blog",
    docs: "Docs",
    signIn: "Autentificare",
    join: "Alătură-te",
    platforms: "Platforme",
    agents: "Agenți",
    useCases: "Cazuri de utilizare",
    resources: "Resurse",
    company: "Companie",
    affiliates: "Afiliere",
    terms: "Termeni",
    privacy: "Confidențialitate",
    acceptableUse: "Utilizare acceptabilă",
    tagline: "Browserul agentic pentru rețele sociale.",
    searchProfiles: "Caută profiluri",
    sendMessages: "Trimite mesaje",
    analyzePosts: "Analizează postări și comentarii",
    marketingAgents: "Agenți AI pentru marketing",
    multiAccount: "Conturi multiple",
    competitorAnalysis: "Analiza concurenței",
    placeholder: "tu@email.com",
    trustStrong: "Nedetectabil",
    trustRest: ", gândit să îți păstreze conturile în siguranță.",
    joinTitle: "Dă-i agentului tău un browser.",
    joinSub:
      "Alătură-te și agentul tău lucrează pe LinkedIn, Sales Navigator, X și Reddit pentru tine, în fundal.",
    emailLabel: "Adresă de e-mail",
    award: "Instrument de agent AI 2026",
    worksFrom: "Funcționează din",
  },
};

/** The landing locale from the URL: /fr/blog → "fr", anything else → "en". */
export function useLandingLocale(): BlogLocale {
  const { pathname } = useLocation();
  const first = pathname.split("/")[1] ?? "";
  return isAlternateLocale(first) ? first : "en";
}

export function useSiteCopy(): SiteCopy {
  return SITE_COPY[useLandingLocale()];
}

// Locales with platform pages: keep in step with PLATFORM_ALTERNATE_LOCALES
// (platform-i18n.ts), not imported here so every page doesn't bundle the
// platform copy.
const PLATFORM_LOCALES: readonly BlogLocale[] = ["en", "fr", "es"];

/** Whether platform pages exist in this locale. */
export function hasLocalizedPlatforms(locale: BlogLocale): boolean {
  return PLATFORM_LOCALES.includes(locale);
}
